import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormResponses } from "@/hooks/useFormResponses";
import { Field, TextInput, YesNo } from "@/components/Field";
import { combineFieldErrors, combineFormError, runUpserts } from "@/lib/validation-errors";

/**
 * Step — Fee reduction request (Part 10 of N-400).
 *
 * USCIS allows reduced filing fees for households at or below 400% of
 * the Federal Poverty Guidelines. Most applicants pay the full fee and
 * skip this entire section — answering "No" to Item 1 is enough.
 *
 * Item 1 is mandatory for everyone (Yes or No). Items 2-5.b are
 * required only if Item 1 = Yes. The mapping mirrors that conditional
 * shape so unset keys produce a blank Part 10.
 */

const FR_KEYS = {
  eligible: "fee_reduction_eligible",
  income: "household_income",
  size: "household_size",
  earners: "household_earners",
  isHead: "is_head_of_household",
  headName: "head_of_household_name",
} as const;

export function StepFeeReduction() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fr = useFormResponses(id);

  const [eligible, setEligible] = useState<boolean | null>(null);
  const [income, setIncome] = useState("");
  const [size, setSize] = useState("");
  const [earners, setEarners] = useState("");
  const [isHead, setIsHead] = useState<boolean | null>(null);
  const [headName, setHeadName] = useState("");

  useEffect(() => {
    if (fr.isLoading) return;
    const e = fr.get<boolean>(FR_KEYS.eligible);
    setEligible(typeof e === "boolean" ? e : null);
    setIncome(fr.getString(FR_KEYS.income));
    setSize(fr.getString(FR_KEYS.size));
    setEarners(fr.getString(FR_KEYS.earners));
    const h = fr.get<boolean>(FR_KEYS.isHead);
    setIsHead(typeof h === "boolean" ? h : null);
    setHeadName(fr.getString(FR_KEYS.headName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...Object.values(FR_KEYS).map((k) => fr.get(k))]);

  const [validationErr, setValidationErr] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);
  const frFieldErrors = combineFieldErrors(saveErrors);
  const frFormError = combineFormError(saveErrors);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErr(null);
    setSaveErrors([]);

    // YesNo "required" guards — HTML can't validate the button-pair.
    if (eligible === null) {
      setValidationErr("Please answer whether your household qualifies for a reduced fee.");
      return;
    }
    if (eligible === true && isHead === null) {
      setValidationErr("Please answer whether you are the head of household.");
      return;
    }

    const upserts: Promise<unknown>[] = [];
    const upsertStr = (key: string, val: string) => {
      if (val) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    const upsertBool = (key: string, val: boolean | null) => {
      if (val !== null) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    upsertBool(FR_KEYS.eligible, eligible);
    if (eligible === true) {
      upsertStr(FR_KEYS.income, income);
      upsertStr(FR_KEYS.size, size);
      upsertStr(FR_KEYS.earners, earners);
      upsertBool(FR_KEYS.isHead, isHead);
      if (isHead === false) {
        upsertStr(FR_KEYS.headName, headName);
      }
    }
    const errors = await runUpserts(upserts);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }
    navigate(`/cases/${id}/wizard/interpreter`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Fee reduction</h1>
        <p className="text-sm text-slate-500 mt-1">
          USCIS may reduce the filing fee if your household earns at or
          below 400% of the Federal Poverty Guidelines. Skip if you
          plan to pay the full fee.
        </p>
      </header>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <legend className="font-medium text-slate-900 px-1">
          Eligibility
        </legend>
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">
            Is your household income at or below 400% of the Federal
            Poverty Guidelines?
          </p>
          <YesNo value={eligible} onChange={setEligible} />
        </div>
      </fieldset>

      {eligible === true && (
        <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <legend className="font-medium text-slate-900 px-1">
            Household details
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Total household income"
              required
              hint="Annual gross — e.g. $48,500"
              error={frFieldErrors?.[FR_KEYS.income]?.message}
            >
              <TextInput
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                placeholder="$"
              />
            </Field>
            <Field
              label="Household size"
              required
              hint="Including yourself, your spouse, and dependents"
              error={frFieldErrors?.[FR_KEYS.size]?.message}
            >
              <TextInput
                type="number"
                min={1}
                value={size}
                onChange={(e) => setSize(e.target.value)}
              />
            </Field>
            <Field
              label="Earners in your household"
              required
              hint="How many household members earn income (including yourself)?"
              className="sm:col-span-2"
              error={frFieldErrors?.[FR_KEYS.earners]?.message}
            >
              <TextInput
                type="number"
                min={1}
                value={earners}
                onChange={(e) => setEarners(e.target.value)}
              />
            </Field>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Are you the head of household?
            </p>
            <YesNo value={isHead} onChange={setIsHead} />
          </div>
          {isHead === false && (
            <Field
              label="Name of head of household"
              required
              error={frFieldErrors?.[FR_KEYS.headName]?.message}
            >
              <TextInput
                value={headName}
                onChange={(e) => setHeadName(e.target.value)}
              />
            </Field>
          )}
        </fieldset>
      )}

      {validationErr && (
        <p className="text-sm text-red-600">{validationErr}</p>
      )}
      {frFormError && (
        <p className="text-sm text-red-600">Could not save: {frFormError}</p>
      )}
      {frFieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/contact`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Contact & signature
        </Link>
        <button
          type="submit"
          disabled={fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </form>
  );
}
